<template>
  <header class="top-header">
    <div class="header-left">
      <img src="@/assets/images/ratify-logo.svg" alt="RATIFY Logo" class="header-logo" @click="goHome" />
    </div>

    <div class="header-right">
      <div class="project-selector">
        <label for="project-select" class="dropdown-label">Project:</label>
        <BFormSelect
          id="project-select"
          v-model="selectedProject"
          :options="projectOptions"
          class="project-dropdown"
        >
          <template #first>
            <option :value="null" disabled>Select Project</option>
          </template>
        </BFormSelect>
      </div>

      <div class="license-info">
        <div class="license-info-row">
          <span class="license-info-label">License:</span>
          <b class="license-info-value">{{ licenseStatus && licenseStatus.loaded ? 'Active' : 'Expired' }}</b>
        </div>
        <!-- <div class="license-info-row" v-if="licenseValidityStatus">
          <span class="license-info-label">Status:</span>
          <b class="license-info-value" :class="{ 'text-warning': hasOverallocation }">{{ licenseValidityStatus }}</b>
        </div> -->
        <div class="license-info-row">
          <span class="license-info-label">DICOM Linked Project:</span>
          <b class="license-info-value">{{ getDicomProjectName }}</b>
        </div>
      </div>

      <button class="license-allocation-btn" @click="openLicenseModal" title="License Allocation">
        <font-awesome-icon :icon="['fas', 'key']" />
      </button>
    </div>
  </header>
</template>

<script setup>
import { computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { BFormSelect } from 'bootstrap-vue-next'

const store = useStore()
const router = useRouter()

// Get selected project ID from store
const selectedProject = computed({
  get: () => store.state.projectSubmission.selectedProjectId,
  set: (value) => {
    store.dispatch('projectSubmission/selectProject', value)
  }
})

// Get projects from store
const projects = computed(() => store.state.projectSubmission.projects || [])

// Map projects to dropdown options
const projectOptions = computed(() => {
  return projects.value.map(project => ({
    value: project._id,
    text: project.name
  }))
})

// License info
const license = computed(() => store.state.license)
const licenseStatus = computed(() => store.state.license?.status)

const hasOverallocation = computed(() => {
  if (licenseStatus.value && licenseStatus.value.loaded && license.value && license.value.info) {
    const info = license.value.info
    return info.userOverallocation > 0 || info.projectOverallocation > 0
  }
  return false
})

const licenseValidityStatus = computed(() => {
  if (licenseStatus.value && licenseStatus.value.loaded) {
    if (license.value && license.value.info) {
      const info = license.value.info
      if (info.userOverallocation > 0) {
        if (info.projectOverallocation > 0) {
          return 'User and Project overallocation!'
        } else {
          return 'User overallocation!'
        }
      } else if (info.projectOverallocation > 0) {
        return 'Project overallocation!'
      } else {
        if (info.licensedUsers >= 0 && info.licensedProjects >= 0) {
          return 'Valid'
        } else {
          return 'Invalid'
        }
      }
    }
  }
  return ''
})

const maxUsers = computed(() => {
  if (licenseStatus.value && licenseStatus.value.loaded && license.value && license.value.info) {
    const info = license.value.info
    if (info.licensedUsers >= 0) {
      return info.licensedUsers === 0 ? 'Unlimited' : info.licensedUsers.toString()
    }
  }
  return ''
})

const maxEvaluations = computed(() => {
  if (licenseStatus.value && licenseStatus.value.loaded && license.value && license.value.info) {
    const info = license.value.info
    if (info.licensedProjects >= 0) {
      return info.licensedProjects === 0 ? 'Unlimited' : info.licensedProjects.toString()
    }
  }
  return ''
})

const getDicomProjectName = computed(() => {
  const dicomProjects = projects.value.filter(p => p.dicom_enabled === true)
  if (dicomProjects.length === 0) {
    return 'None'
  } else if (dicomProjects.length === 1) {
    return dicomProjects[0].name
  } else {
    return dicomProjects.length + ' Project'
  }
})

// Navigate to project select (home)
const goHome = () => {
  store.dispatch('projectSubmission/selectProject', null)
  router.push({ name: 'Workspace' })
}

// Open license allocation modal
const openLicenseModal = () => {
  store.dispatch('workspacePanel/setShowLicenseModal', true)
}

// Watch for project selection changes and navigate to dashboard
watch(selectedProject, (newValue) => {
  if (newValue) {
    console.log('Selected project changed:', newValue)

    // Find the selected project to get its type
    const project = projects.value.find(p => p._id === newValue)
    if (project) {
      // Navigate to appropriate dashboard based on project type
      if (project.type === 'retrospective') {
        router.push({ name: 'RetrospectiveDashboard', params: { projectId: newValue } })
      } else if (project.type === 'prospective') {
        router.push({ name: 'ProspectiveDashboard', params: { projectId: newValue } })
      }
    }
  }
})
</script>

<style scoped>
.top-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 112px;
  background-color: var(--color-bg-secondary);
  border-bottom: 1px solid #1e4f78;
  box-shadow: 0 2px 8px rgba(30, 79, 120, 0.3);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem;
  z-index: 999;
  transition: all 0.3s ease;
}

.header-left {
  display: flex;
  align-items: center;
  padding-left: 0.5rem;
  flex: 0 0 auto;
}

.header-logo {
  height: 100px;
  width: auto;
  cursor: pointer;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 2rem;
  padding-right: 0.5rem;
  flex: 1;
  justify-content: flex-end;
}

.license-info {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  text-align: left;
  font-size: 0.75rem;
}

.license-info-row {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-start;
  align-items: baseline;
}

.license-info-label {
  color: var(--color-text-secondary);
  font-weight: var(--font-weight-normal);
}

.license-info-value {
  color: var(--color-text-primary);
  font-weight: var(--font-weight-semibold);
}

.license-info-value.text-warning {
  color: #fbbf24 !important;
}

.project-selector {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.dropdown-label {
  color: var(--color-text-primary);
  font-size: 0.9rem;
  font-weight: var(--font-weight-normal);
  margin: 0;
}

.project-dropdown {
  width: 200px;
  border: 2px solid var(--color-border-primary);
  border-radius: var(--border-radius-small);
  background-color: var(--color-bg-secondary);
  color: var(--color-text-primary);
  padding: 0.5rem;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.project-dropdown:focus {
  border-color: var(--color-accent-hover);
  outline: none;
  box-shadow: var(--shadow-focus);
}

.project-dropdown option {
  background-color: var(--color-bg-secondary);
  color: var(--color-text-primary);
}

.project-dropdown option:disabled {
  color: var(--color-text-secondary);
  font-style: italic;
}

.project-dropdown:invalid {
  color: var(--color-text-secondary);
}

.license-allocation-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  border: 1px solid var(--color-border-secondary);
  border-radius: var(--border-radius-small);
  color: var(--color-text-secondary);
  padding: 0.25rem 0.75rem;
  cursor: pointer;
  font-size: 0.75rem;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.license-allocation-btn:hover {
  background-color: var(--color-bg-primary);
  border-color: var(--color-accent-primary);
  color: var(--color-accent-primary);
}
</style>
